import { useRef, useEffect, useMemo, useCallback, useState } from "react";
import { ChatMessage } from "@/zapi-frontend/shared/ui/custom/ChatMessage";
import { ChatMessage as ChatMessageType } from "@/zapi-frontend/stores/chatStore";
import { ChevronDown } from "lucide-react";
import { AiMessage, PrereqChapterList, ProjectGenerationProgress } from "@/zapi-frontend/shared/ui/chat";

export type TGenerationProgressProps = {
  projectId: string;
  onCancel?: () => void;
  isCancelling?: boolean;
};

interface ChatMessageListProps {
  messages: ChatMessageType[];
  loadingMessageId: string | null;
  isGeneratingChapter: boolean;
  progressMessage?: string | null;
  prereqsChaptersToBeGenerated: string[] | null;
  allPrereqChapters: string[] | null;
  isAskStreaming: boolean;
  webLinkEnabled?: boolean;
  messageAttachmentsEnabled?: boolean;
  generationProgress?: TGenerationProgressProps;
}

const hashMessages = (messages: ChatMessageType[]) => {
  return messages.map((msg) => msg.id).join("-");
};

export function ChatMessageList({
  messages,
  loadingMessageId,
  isGeneratingChapter,
  progressMessage,
  prereqsChaptersToBeGenerated,
  allPrereqChapters,
  isAskStreaming,
  webLinkEnabled = false,
  messageAttachmentsEnabled = false,
  generationProgress
}: ChatMessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const wasAskStreamingRef = useRef(false);
  const [showScrollDown, setShowScrollDown] = useState(false);

  const messagesHash = useMemo(() => hashMessages(messages), [messages]);
  const contentRef = useRef<HTMLDivElement>(null);

  const checkIfScrollable = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    setShowScrollDown(!isNearBottom && container.scrollHeight > container.clientHeight);
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView();
  }, []);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    const observer = new ResizeObserver(checkIfScrollable);
    observer.observe(content);
    return () => observer.disconnect();
  }, [checkIfScrollable]);

  useEffect(() => {
    const justFinishedAskStreaming = wasAskStreamingRef.current && !isAskStreaming;
    wasAskStreamingRef.current = isAskStreaming;

    if (justFinishedAskStreaming) {
      setTimeout(checkIfScrollable, 150);
      return;
    }

    const timeout = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timeout);
  }, [
    messagesHash,
    loadingMessageId,
    progressMessage,
    prereqsChaptersToBeGenerated,
    isAskStreaming,
    scrollToBottom,
    checkIfScrollable
  ]);

  return (
    <div className="relative flex-1 min-h-0">
      {showScrollDown && (
        <button
          data-testid="chat-message-list-scroll-down-button"
          onClick={() => {
            scrollToBottom();
            setShowScrollDown(false);
          }}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-white border border-gray-200 shadow-md rounded-full p-2 hover:bg-gray-50 transition-colors"
        >
          <ChevronDown className="w-5 h-5 text-gray-600" />
        </button>
      )}
      <div ref={scrollContainerRef} onScroll={checkIfScrollable} className="h-full overflow-y-auto px-4 py-4">
        <div ref={contentRef}>
          {generationProgress && <ProjectGenerationProgress {...generationProgress} />}
          {messages.map((message) => (
            <ChatMessage
              key={message.id}
              message={message}
              isLoading={message.id === loadingMessageId}
              progressMessage={message.id === loadingMessageId ? progressMessage : undefined}
              webLinkEnabled={webLinkEnabled}
              messageAttachmentsEnabled={messageAttachmentsEnabled}
            />
          ))}
          {loadingMessageId &&
            isGeneratingChapter &&
            allPrereqChapters &&
            allPrereqChapters.length > 0 && (
              <AiMessage className="w-full">
                <PrereqChapterList
                  chapters={allPrereqChapters}
                  remainingChapters={prereqsChaptersToBeGenerated ?? []}
                  variant="status"
                />
              </AiMessage>
            )}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
}
